import { Card, Icon } from "@atoms";
import { H1 } from "@quarks";
import { Canvas, Fullbody, Heading } from "@templates";
import { useTranslation } from "react-i18next";
import { Avatar, Button, Skeleton } from "@molecules";
import { useNavigate, useParams } from "react-router-dom";
import { BreadCrumbs, useConnectBreadCrumbs } from "./BreadCrumbs";
import { UserSchedule } from "./components/UserSchedule";
import { PersonConnectionButton } from "./components/PersonConnectionButton";
import { PersonPageSkeleton } from "./components/PersonPageSkeleton";
import { useEffect } from "react";
import { usePersonConnection } from "@hooks";
import { actions, getUser, getUserProfileRequestByUserId, useAppDispatch, useAppSelector } from "@lib/store";
import { GROUP_DATA_DATE_RANGE } from "@lib/store/src/data/groups/constants";
import { notFoundErrorName } from "@lib/infrastructure";

// Any person in the tenant, loaded from `GET /users/{userId}` rather than from the viewer's connections or groups
export const PersonPage = () => {
  const { userId = "" } = useParams<{ userId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { paths } = useConnectBreadCrumbs();
  const goBackPath = paths.at(-2) ?? "/people";

  const request = useAppSelector(state => getUserProfileRequestByUserId(state, userId));
  const person = request?.data ?? null;
  const canSeeSchedule = person?.canSeeSchedule ?? false;

  // A group shows its members, and the viewer is one of them
  const isViewer = useAppSelector(state => getUser(state).id === userId);
  const { relation, isSending, connect, accept } = usePersonConnection(userId);
  const isConnected = relation === "connected";

  // Again once they're connected, because that can let the viewer see the person's schedule
  useEffect(
    function loadPerson() {
      dispatch(actions.loadUserProfile({ userId }));
    },
    [dispatch, userId, isConnected],
  );

  // The endpoint refuses the schedule of a person the viewer may not see, so don't ask for it
  useEffect(
    function loadSchedule() {
      if (!canSeeSchedule) return;

      dispatch(actions.loadConnectWorkspaceReservations({ userId, range: GROUP_DATA_DATE_RANGE }));
      dispatch(actions.loadConnectWorkdays({ userId, range: GROUP_DATA_DATE_RANGE }));
    },
    [dispatch, userId, canSeeSchedule],
  );

  const isLoading = !person && request?.status !== "rejected";

  const isNotFound = request?.status === "rejected" && request.errorName === notFoundErrorName;

  return (
    <>
      <Heading className={"px-4 bg-beige-200 border-none justify-between"}>
        <div className={"col-span-full flex w-full grid-cols-2 justify-between items-center"}>
          <button onClick={() => navigate(goBackPath)}>
            <Icon
              icon="caretLeft"
              className="size-5"
            />
          </button>

          <h4>{t("hereAndNow:Coworker")}</h4>

          <span className="size-5" />
        </div>
      </Heading>

      <Canvas />

      <Fullbody className="p-4 lg:p-8 md:py-14">
        <div className="md:flex justify-center flex-1">
          <div className="grid gap-4 sm:gap-[var(--gutterWidth)] grid-cols-1 md:grid-cols-[minmax(0,var(--maxWidth))] justify-center">
            {isLoading ? <Skeleton className="hidden sm:block h-5 w-40" /> : <BreadCrumbs className="hidden sm:flex" />}

            <div className="grid gap-4 sm:gap-[var(--gutterWidth)] grid-cols-[minmax(0,var(--mainWidth))] col-span-full justify-center">
              {isLoading ? (
                <PersonPageSkeleton hasConnectionButton={!isViewer} />
              ) : (
                <>
                  <Card className="bg-beige-200 py-8 px-6 grid grid-cols-[auto_1fr_auto] items-center gap-4 shadow-none">
                    <Avatar
                      user={person ? { ...person, initials: person.initials ?? "" } : { email: "", initials: "" }}
                      size="xlarge"
                    />
                    <div className="flex flex-col gap-1 items-center sm:items-start">
                      <H1 className="col-span-full">{person?.name ?? t("groups:UnknownUser")}</H1>
                    </div>

                    {person && !isViewer && (
                      <PersonConnectionButton
                        relation={relation}
                        isSending={isSending}
                        onConnect={connect}
                        onAccept={accept}
                      />
                    )}
                  </Card>

                  {!person && (
                    <Card className="py-8 px-6 flex flex-col items-center gap-4 text-center">
                      {isNotFound ? (
                        <p className="text-mapiq-black-500">{t("connection:PersonNotFound")}</p>
                      ) : (
                        <>
                          <p className="text-mapiq-black-500">{t("connection:PersonLoadFailed")}</p>

                          <Button
                            button="secondary"
                            size="small"
                            onClick={() => dispatch(actions.loadUserProfile({ userId }))}>
                            {t("workplace:TryAgain")}
                          </Button>
                        </>
                      )}
                    </Card>
                  )}

                  {person && (
                    <>
                      {canSeeSchedule && (
                        <div className={"grid w-full gap-4 sm:gap-[var(--gutterWidth)] md:col-start-1 order-1"}>
                          <UserSchedule user={person} />
                        </div>
                      )}

                      {!canSeeSchedule && !isViewer && (
                        <Card className="py-8 px-6 text-center text-mapiq-black-500">
                          {t("connection:ConnectToSeeSchedule", { name: person.name })}
                        </Card>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Fullbody>
    </>
  );
};
